import { useEffect, useState } from 'react';
import { apiClient } from './api';

/**
 * Phase 2 CMS: fetch a page's content from /api/content/{page_slug}
 * with a frozen fallback. Shape of the returned object is
 *   { [section_slug]: data | null }
 * and the hook merges CMS data over the fallback section-by-section so
 * an admin can leave some sections unedited without blanking them.
 *
 * Deliberately simple: no suspense, no SWR. CRA client-side render +
 * one fetch per route + an in-memory cache per page_slug for the
 * lifetime of the SPA load. A 60s backend Cache-Control header lets
 * CloudFront absorb repeat hits across visitors.
 */

const _cache = new Map(); // page_slug -> { data, inflight }

const mergeSections = (fallback, cms) => {
    if (!cms || typeof cms !== 'object') return fallback || {};
    const out = { ...(fallback || {}) };
    for (const key of Object.keys(cms)) {
        const value = cms[key];
        if (value && typeof value === 'object' && !Array.isArray(value) && !Array.isArray(fallback?.[key])) {
            // Shallow-merge object sections so partial admin edits still
            // preserve any fallback keys the admin hasn't touched.
            out[key] = { ...(fallback?.[key] || {}), ...value };
        } else if (value !== null && value !== undefined) {
            out[key] = value;
        }
    }
    return out;
};

export const usePageContent = (pageSlug, fallback = {}) => {
    const [state, setState] = useState(() => {
        const cached = _cache.get(pageSlug);
        return cached?.data ? mergeSections(fallback, cached.data) : fallback;
    });

    useEffect(() => {
        let cancelled = false;
        const cached = _cache.get(pageSlug);
        if (cached?.data) {
            setState(mergeSections(fallback, cached.data));
            return () => { cancelled = true; };
        }
        const inflight = cached?.inflight ||
            apiClient.get(`/content/${pageSlug}`).then((r) => r.data).catch(() => null);
        _cache.set(pageSlug, { data: cached?.data, inflight });
        inflight.then((data) => {
            if (cancelled) return;
            if (data) {
                _cache.set(pageSlug, { data, inflight: null });
                setState(mergeSections(fallback, data));
            } else {
                _cache.set(pageSlug, { data: null, inflight: null });
            }
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSlug]);

    return state;
};

/** Imperative helper for cases where a component needs the data outside
 *  of a React effect (e.g. the admin editor's "revert to default"). */
export const invalidatePageContent = (pageSlug) => {
    _cache.delete(pageSlug);
};
